import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, User, Scissors, CalendarDays, Phone, Wallet, Loader2, CheckCircle2, Copy, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { publicApi, errText } from "@/lib/api";
import { priceLabel, buildDays, to12h, prettyDate } from "@/lib/format";

const STEPS = ["Barber", "Service", "Date & Time", "Details", "Payment", "Confirm"];

export const BookingWizard = ({ barbers, services, settings }) => {
  const [step, setStep] = useState(0);
  const [barber, setBarber] = useState(null);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [payment, setPayment] = useState(null);
  const [whishRef, setWhishRef] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const rate = settings?.exchange_rate || 89000;
  const days = buildDays(21);
  const closedKeys = Object.entries(settings?.working_hours || {})
    .filter(([, v]) => v.closed)
    .map(([k]) => k);
  const blockedDates = settings?.blocked_dates || [];

  const loadSlots = useCallback(async () => {
    if (!barber || !date) return;
    setLoadingSlots(true);
    try {
      const res = await publicApi.availability(barber.id, date);
      setSlots(res.slots);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoadingSlots(false);
    }
  }, [barber, date]);

  useEffect(() => {
    if (step === 2 && barber && date) loadSlots();
  }, [step, barber, date, loadSlots]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return !!barber;
    if (step === 1) return !!service;
    if (step === 2) return !!date && !!time;
    if (step === 3) return name.trim().length >= 2 && phone.trim().length >= 6;
    if (step === 4) return payment === "pay_at_shop" || (payment === "whish" && whishRef.trim().length >= 3);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await publicApi.createAppointment({
        barber_id: barber.id,
        service_id: service.id,
        date,
        time,
        customer_name: name,
        customer_phone: phone,
        payment_method: payment,
        whish_reference: payment === "whish" ? whishRef : null,
      });
      setConfirmed(res);
      setStep(5);
    } catch (e) {
      toast.error(errText(e));
      if (e?.response?.status === 409) {
        setStep(2);
        setTime(null);
        loadSlots();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0); setBarber(null); setService(null); setDate(null); setTime(null);
    setName(""); setPhone(""); setPayment(null); setWhishRef(""); setConfirmed(null);
  };

  const dateDisabled = (d) => closedKeys.includes(d.weekdayKey) || blockedDates.includes(d.date);

  return (
    <div className="mx-auto w-full max-w-3xl" data-testid="booking-wizard">
      {/* Stepper */}
      <div className="mb-8 flex items-center justify-between overflow-x-auto no-scrollbar">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-1 items-center">
            <div className="flex flex-col items-center gap-1.5 min-w-[52px]">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold transition-colors duration-300 ${
                  i < step
                    ? "border-gold bg-gold text-obsidian-900"
                    : i === step
                    ? "border-gold text-gold"
                    : "border-white/15 text-gray-500"
                }`}
              >
                {i < step ? <Check size={15} /> : i + 1}
              </div>
              <span className={`hidden sm:block text-[10px] font-mono uppercase tracking-wider ${i === step ? "text-gold" : "text-gray-500"}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`mx-1 h-px flex-1 ${i < step ? "bg-gold" : "bg-white/10"}`} />
            )}
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-white/10 bg-obsidian-card p-5 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {/* STEP 0: Barber */}
            {step === 0 && (
              <div>
                <StepHead icon={User} title="Choose your barber" sub="Select a master for your session" />
                <div className="grid gap-3 sm:grid-cols-3">
                  {barbers.map((b) => (
                    <button
                      key={b.id}
                      data-testid={`select-barber-${b.id}`}
                      onClick={() => setBarber(b)}
                      className={`group overflow-hidden rounded-2xl border text-left transition-colors duration-200 ${
                        barber?.id === b.id ? "border-gold" : "border-white/10 hover:border-gold/50"
                      }`}
                    >
                      <div className="h-32 w-full overflow-hidden bg-obsidian-700">
                        <img src={b.image_url} alt={b.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="p-3">
                        <div className="font-display text-base font-bold text-white">{b.name}</div>
                        <div className="text-xs text-gray-400">{b.role}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  data-testid="select-barber-any"
                  onClick={() => setBarber({ id: "any", name: "Any Available Barber", role: "We'll assign the earliest free master" })}
                  className={`mt-3 flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors duration-200 ${
                    barber?.id === "any" ? "border-gold bg-gold-soft" : "border-white/10 hover:border-gold/50"
                  }`}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gold/30 bg-gold-soft text-gold">
                    <Users size={20} />
                  </div>
                  <div>
                    <div className="font-display text-base font-bold text-white">Any Available Barber</div>
                    <div className="text-xs text-gray-400">Fastest option — we'll assign the earliest free master</div>
                  </div>
                </button>
              </div>
            )}

            {/* STEP 1: Service */}
            {step === 1 && (
              <div>
                <StepHead icon={Scissors} title="Select a service" sub="Every session is one hour" />
                <div className="space-y-3">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`select-service-${s.id}`}
                      onClick={() => setService(s)}
                      className={`flex w-full items-center justify-between gap-4 rounded-2xl border p-4 text-left transition-colors duration-200 ${
                        service?.id === s.id ? "border-gold bg-gold-soft" : "border-white/10 hover:border-gold/50"
                      }`}
                    >
                      <div>
                        <div className="font-semibold text-white">{s.name}</div>
                        <div className="text-xs text-gray-400">{s.description}</div>
                      </div>
                      <div className="whitespace-nowrap text-right">
                        <div className="font-mono text-sm font-semibold text-gold">${s.price_usd}</div>
                        <div className="font-mono text-[10px] text-gray-500">{(s.price_usd * rate).toLocaleString()} L.L.</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2: Date & Time */}
            {step === 2 && (
              <div>
                <StepHead icon={CalendarDays} title="Pick date & time" sub="Tue–Sun, 10:00 AM – 12:00 AM · Mon closed" />
                <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar pb-2">
                  {days.map((d) => {
                    const disabled = dateDisabled(d);
                    return (
                      <button
                        key={d.date}
                        disabled={disabled}
                        data-testid={`select-date-${d.date}`}
                        onClick={() => { setDate(d.date); setTime(null); }}
                        className={`flex min-w-[62px] flex-col items-center rounded-xl border px-2 py-3 transition-colors duration-200 ${
                          disabled
                            ? "cursor-not-allowed border-white/5 opacity-30"
                            : date === d.date
                            ? "border-gold bg-gold text-obsidian-900"
                            : "border-white/10 hover:border-gold/50"
                        }`}
                      >
                        <span className="font-mono text-[10px] uppercase tracking-wider">{d.dayShort}</span>
                        <span className="font-display text-lg font-bold">{d.dayNum}</span>
                        <span className="text-[10px]">{d.month}</span>
                      </button>
                    );
                  })}
                </div>

                {date && (
                  <div>
                    <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-gold">Available times</div>
                    {loadingSlots ? (
                      <div className="flex items-center gap-2 py-6 text-gray-400"><Loader2 className="animate-spin" size={18} /> Loading slots…</div>
                    ) : slots.length === 0 ? (
                      <div className="rounded-xl border border-white/10 bg-obsidian-800 p-4 text-sm text-gray-400" data-testid="no-slots-message">
                        No available times for this day. Please choose another date.
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {slots.map((t) => (
                          <button
                            key={t}
                            data-testid={`select-time-${t}`}
                            onClick={() => setTime(t)}
                            className={`rounded-xl border py-2.5 font-mono text-sm transition-colors duration-200 ${
                              time === t ? "border-gold bg-gold text-obsidian-900" : "border-white/10 text-gray-200 hover:border-gold/50"
                            }`}
                          >
                            {to12h(t)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3: Details */}
            {step === 3 && (
              <div>
                <StepHead icon={Phone} title="Your details" sub="So we know who's coming in" />
                <div className="space-y-4">
                  <Field label="Full Name">
                    <input
                      data-testid="input-customer-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Karim Haddad"
                      className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-4 py-3 text-white outline-none focus:border-gold"
                    />
                  </Field>
                  <Field label="Phone Number">
                    <input
                      data-testid="input-customer-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+961 70 000 000"
                      className="w-full rounded-xl border border-white/10 bg-obsidian-800 px-4 py-3 font-mono text-white outline-none focus:border-gold"
                    />
                  </Field>
                </div>
              </div>
            )}

            {/* STEP 4: Payment */}
            {step === 4 && (
              <div>
                <StepHead icon={Wallet} title="Payment method" sub="Choose how you'd like to pay" />
                <div className="space-y-3">
                  <button
                    data-testid="select-payment-whish"
                    onClick={() => setPayment("whish")}
                    className={`w-full rounded-2xl border p-4 text-left transition-colors duration-200 ${
                      payment === "whish" ? "border-gold bg-gold-soft" : "border-white/10 hover:border-gold/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">Whish Money</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-gold">Online</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Pay via Whish and enter your transaction reference. The shop will verify it.</p>
                  </button>

                  {payment === "whish" && (
                    <div className="rounded-2xl border border-gold/30 bg-obsidian-800 p-4">
                      <p className="text-sm text-gray-300">
                        Transfer the amount to Whish number{" "}
                        <span className="font-mono font-semibold text-gold">{settings?.whish_number}</span>, then enter your reference below.
                      </p>
                      <input
                        data-testid="input-whish-reference"
                        value={whishRef}
                        onChange={(e) => setWhishRef(e.target.value)}
                        placeholder="Whish transaction / reference number"
                        className="mt-3 w-full rounded-xl border border-white/10 bg-obsidian-900 px-4 py-3 font-mono text-white outline-none focus:border-gold"
                      />
                    </div>
                  )}

                  <button
                    data-testid="select-payment-shop"
                    onClick={() => setPayment("pay_at_shop")}
                    className={`w-full rounded-2xl border p-4 text-left transition-colors duration-200 ${
                      payment === "pay_at_shop" ? "border-gold bg-gold-soft" : "border-white/10 hover:border-gold/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">Pay at the Shop</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400">Cash</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Pay upon arrival in cash (USD / LBP).</p>
                  </button>
                </div>

                <Summary barber={barber} service={service} date={date} time={time} rate={rate} />
              </div>
            )}

            {/* STEP 5: Confirmation */}
            {step === 5 && confirmed && (
              <div className="text-center" data-testid="booking-confirmation">
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }}>
                  <CheckCircle2 className="mx-auto text-gold" size={64} />
                </motion.div>
                <h3 className="mt-4 font-display text-2xl font-bold text-white">Appointment Confirmed</h3>
                <p className="mt-1 text-sm text-gray-400">See you soon, {confirmed.customer_name.split(" ")[0]}!</p>

                <div className="mx-auto mt-6 max-w-sm rounded-2xl border border-gold/30 bg-obsidian-800 p-5 text-left">
                  <Row k="Barber" v={confirmed.barber_name} />
                  <Row k="Service" v={confirmed.service_name} />
                  <Row k="Date" v={prettyDate(confirmed.date)} />
                  <Row k="Time" v={to12h(confirmed.time)} />
                  <Row k="Total" v={priceLabel(confirmed.price_usd, rate)} />
                  <Row k="Payment" v={confirmed.payment_method === "whish" ? "Whish Money (pending verification)" : "Pay at shop"} />
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-obsidian-900 px-3 py-2">
                    <span className="font-mono text-xs text-gray-400">#{confirmed.id.slice(0, 8).toUpperCase()}</span>
                    <button
                      data-testid="copy-booking-code"
                      onClick={() => { navigator.clipboard.writeText(confirmed.id); toast.success("Booking code copied"); }}
                      className="flex items-center gap-1 text-xs text-gold hover:text-gold-hover"
                    >
                      <Copy size={13} /> Copy
                    </button>
                  </div>
                </div>

                <button
                  data-testid="book-another-btn"
                  onClick={reset}
                  className="mt-6 rounded-full border border-gold/40 px-6 py-2.5 text-sm font-semibold text-gold transition-colors duration-200 hover:bg-gold-soft"
                >
                  Book another appointment
                </button>

                {settings?.contact?.whatsapp && (
                  <a
                    data-testid="whatsapp-confirm-btn"
                    href={`https://wa.me/${settings.contact.whatsapp}?text=${encodeURIComponent(
                      `Hello Ali Malak Barbershop! I've booked an appointment:\n\nName: ${confirmed.customer_name}\nBarber: ${confirmed.barber_name}\nService: ${confirmed.service_name}\nDate: ${prettyDate(confirmed.date)}\nTime: ${to12h(confirmed.time)}\nRef: #${confirmed.id.slice(0, 8).toUpperCase()}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full bg-[#25D366] px-6 py-2.5 text-sm font-bold text-black transition-transform duration-200 hover:scale-[1.03]"
                  >
                    <MessageCircle size={16} /> Send confirmation on WhatsApp
                  </a>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        {step < 5 && (
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              data-testid="wizard-back-btn"
              onClick={back}
              disabled={step === 0}
              className={`flex items-center gap-1 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                step === 0 ? "cursor-not-allowed text-gray-600" : "text-gray-300 hover:text-gold"
              }`}
            >
              <ChevronLeft size={16} /> Back
            </button>

            {step < 4 ? (
              <button
                data-testid="wizard-next-btn"
                onClick={next}
                disabled={!canProceed()}
                className={`rounded-full px-8 py-3 text-sm font-bold transition-colors duration-200 ${
                  canProceed() ? "bg-gold text-obsidian-900 hover:bg-gold-hover" : "cursor-not-allowed bg-white/10 text-gray-500"
                }`}
              >
                Continue
              </button>
            ) : (
              <button
                data-testid="wizard-confirm-btn"
                onClick={submit}
                disabled={!canProceed() || submitting}
                className={`flex items-center gap-2 rounded-full px-8 py-3 text-sm font-bold transition-colors duration-200 ${
                  canProceed() && !submitting ? "bg-gold text-obsidian-900 hover:bg-gold-hover" : "cursor-not-allowed bg-white/10 text-gray-500"
                }`}
              >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                Confirm Booking
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StepHead = ({ icon: Icon, title, sub }) => (
  <div className="mb-6 flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gold/30 bg-gold-soft text-gold">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-display text-xl font-bold text-white">{title}</h3>
      <p className="text-xs text-gray-400">{sub}</p>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-gold">{label}</label>
    {children}
  </div>
);

const Summary = ({ barber, service, date, time, rate }) => (
  <div className="mt-5 rounded-2xl border border-white/10 bg-obsidian-800 p-4">
    <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-gold">Summary</div>
    <Row k="Barber" v={barber?.name} />
    <Row k="Service" v={service?.name} />
    <Row k="When" v={`${prettyDate(date)} · ${to12h(time)}`} />
    <Row k="Total" v={service ? priceLabel(service.price_usd, rate) : "—"} />
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
    <span className="text-xs text-gray-400">{k}</span>
    <span className="text-sm font-medium text-white text-right">{v}</span>
  </div>
);
